/* ============================================================================
   sr-megamenu.js — open/close behaviour for the Sapphire Row mega menu.
   ----------------------------------------------------------------------------
   Progressive enhancement: with JS off, every trigger is still an ordinary <a>
   to a real landing page (/collections/all-jewelry, /collections/all-gemstones,
   …), so the nav degrades to a working set of links rather than to nothing.

   ⚠️ HOVER ALONE IS NOT AN INTERACTION MODEL. Three things have to open this
   panel and they are not the same event:
     - POINTER  hover, with intent delays (see below)
     - KEYBOARD focus and Enter/Space, Escape to close, focus returned to trigger
     - TOUCH    a first tap opens, a second follows the link — a device with no
                hover would otherwise navigate away before the panel is ever seen
   ========================================================================= */
(function () {
  'use strict';

  var nav = document.querySelector('.srm-nav');
  if (!nav) return;

  var stage = nav.querySelector('[data-srm-stage]');
  var items = [].slice.call(nav.querySelectorAll('.srm-nav__item[data-srm]'));
  if (!stage || !items.length) return;

  /* OPEN_DELAY exists because the triggers sit side by side: crossing "Jewelry"
     on the way to "Learn" should not flash three panels. CLOSE_DELAY covers the
     diagonal — a pointer travelling from the trigger to the first link in the
     panel briefly leaves both elements, and without the grace period the panel
     shuts under the cursor. These are the two classic mega-menu failures. */
  var OPEN_DELAY = 110;
  var CLOSE_DELAY = 220;

  var openTimer = null;
  var closeTimer = null;
  var current = null;
  var returningFocus = false;   // M156: true only while Escape hands focus back

  var mq = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* Feed the CSS the real distance from the top of the window to the top of the
     panel, so `max-height: calc(100vh - var(--srm-top))` can never be reached by
     a panel taller than the room actually available. Measured rather than
     assumed: the header is sticky and changes height on scroll, and the
     announcement bar is a separate section that may or may not be enabled. */
  function measure() {
    var top = Math.round(stage.getBoundingClientRect().top);
    /* Below zero once the sticky header has scrolled up past the top edge —
       clamp, or the cap grows larger than the window. */
    nav.style.setProperty('--srm-top', Math.max(top, 0) + 'px');

    /* How far the nav is inset from each edge, so the panel can bleed back out
       to full width. `clientWidth` on the root element EXCLUDES the scrollbar;
       `window.innerWidth` includes it, and using that here would push the panel
       past the right edge and give the page a horizontal scrollbar. Left and
       right are measured separately rather than assumed symmetric — a scrollbar
       makes them differ by its own width. */
    var r = nav.getBoundingClientRect();
    var vw = document.documentElement.clientWidth;
    nav.style.setProperty('--srm-bleed-l', Math.max(0, Math.round(r.left)) + 'px');
    nav.style.setProperty('--srm-bleed-r', Math.max(0, Math.round(vw - r.right)) + 'px');
  }
  measure();
  window.addEventListener('resize', measure);
  window.addEventListener('scroll', measure, { passive: true });

  /* 🚨 A `loading="lazy"` IMAGE INSIDE A HIDDEN PANEL NEVER LOADS, so the first
     open of a panel showed eighteen empty circles that filled in one by one
     while the shopper was already reading the list. `hidden` is `display:none`
     and lazy images in a display:none subtree are deferred indefinitely — the
     panel becoming visible is what finally starts the fetch, i.e. exactly too
     late.

     Warming on first hover of the NAV (not of a trigger) buys the whole
     OPEN_DELAY plus the pointer's travel time, and an eager image does load
     inside display:none, which is what makes this work at all. The cost is ~50KB
     of 120px thumbnails, and only for a visitor who has aimed at the menu.
     Deliberately not done at page load: most visitors never open a panel. */
  var warmed = false;
  function warm() {
    if (warmed) return;
    warmed = true;
    [].forEach.call(stage.querySelectorAll('img[loading="lazy"]'), function (img) {
      img.removeAttribute('loading');
    });
  }
  nav.addEventListener('mouseenter', warm);
  nav.addEventListener('focusin', warm);
  nav.addEventListener('touchstart', warm, { passive: true });

  function panelFor(key) {
    return stage.querySelector('[data-srm-panel="' + key + '"]');
  }

  function open(item) {
    var key = item.getAttribute('data-srm');
    var panel = panelFor(key);
    if (!panel || current === item) return;

    close(true);

    item.classList.add('is-open');
    item.querySelector('.srm-nav__link').setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    /* Force a reflow between `hidden = false` and the class that animates it.
       Without it the browser coalesces both into one style recalculation and
       the panel simply appears — the transition never runs. */
    void panel.offsetHeight;
    panel.classList.add('is-open');
    current = item;
  }

  function close(immediate) {
    if (!current) return;
    var item = current;
    var panel = panelFor(item.getAttribute('data-srm'));
    item.classList.remove('is-open');
    item.querySelector('.srm-nav__link').setAttribute('aria-expanded', 'false');
    current = null;
    if (!panel) return;
    panel.classList.remove('is-open');
    if (immediate) {
      panel.hidden = true;
      return;
    }
    /* Wait out the fade before re-hiding, or the panel disappears instantly and
       the transition is only ever seen on the way in. */
    window.setTimeout(function () {
      if (!panel.classList.contains('is-open')) panel.hidden = true;
    }, 220);
  }

  function clearTimers() {
    window.clearTimeout(openTimer);
    window.clearTimeout(closeTimer);
  }

  function scheduleOpen(item) {
    clearTimers();
    openTimer = window.setTimeout(function () { open(item); }, OPEN_DELAY);
  }

  function scheduleClose() {
    clearTimers();
    closeTimer = window.setTimeout(function () { close(); }, CLOSE_DELAY);
  }

  items.forEach(function (item) {
    var link = item.querySelector('.srm-nav__link');

    item.addEventListener('mouseenter', function () {
      if (!mq.matches) return;
      scheduleOpen(item);
    });

    item.addEventListener('mouseleave', function () {
      if (!mq.matches) return;
      scheduleClose();
    });

    /* Touch: first tap reveals, second navigates. Checking `current !== item`
       rather than a stored flag means a tap on a DIFFERENT trigger also opens
       rather than following, which is what a shopper expects from a row of
       peers. */
    link.addEventListener('click', function (event) {
      if (mq.matches) return;
      if (current !== item) {
        event.preventDefault();
        clearTimers();
        open(item);
      }
    });

    link.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        /* Enter on a link normally navigates. Opening instead would strand a
           keyboard user with no way to reach the landing page, so only the
           Down arrow opens and Enter is left alone. */
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        clearTimers();
        open(item);
        var first = panelFor(item.getAttribute('data-srm')).querySelector('a');
        if (first) first.focus();
      }
    });
  });

  /* A panel opened by focus must stay open while focus is inside it, and close
     when focus leaves the nav entirely — `focusout` fires before the new focus
     lands, hence the deferred check on document.activeElement. */
  nav.addEventListener('focusin', function (event) {
    if (returningFocus) return;
    var item = event.target.closest('.srm-nav__item[data-srm]');
    if (item) {
      clearTimers();
      open(item);
      return;
    }
    if (event.target.closest('.srm__panel')) clearTimers();
  });

  nav.addEventListener('focusout', function () {
    window.setTimeout(function () {
      if (!nav.contains(document.activeElement)) close();
    }, 0);
  });

  stage.addEventListener('mouseenter', clearTimers);
  stage.addEventListener('mouseleave', function () {
    if (mq.matches) scheduleClose();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' || !current) return;
    var trigger = current.querySelector('.srm-nav__link');
    clearTimers();
    close(true);
    if (trigger) {
      returningFocus = true;
      try { trigger.focus(); } finally { returningFocus = false; }
    }
  });

  /* A tap anywhere outside closes on touch, where there is no mouseleave. */
  document.addEventListener('click', function (event) {
    if (current && !nav.contains(event.target)) {
      clearTimers();
      close(true);
    }
  });
})();
